import apiClient from '../../apis';
import { getPersistenceNamespace } from '../../stores/helpers/persistenceHelpers';

/**
 * Build jobs (#1621, B7a) as the status read model shows them (#1624, R6): `GET /api/builder/jobs` (the list, brief),
 * `GET …/jobs/{id}` (`?view=brief` for polls). Every GET carries a validator of exactly the body this viewer gets; a
 * poll sends it back as `If-None-Match` and a 304 means nothing shown has changed. A refusal is a code, never text the
 * server wrote. The shared client's interceptor (`apis.ts`) unwraps the `{success, data}` envelope.
 */

export type BuildState = 'queued' | 'running' | 'ready' | 'published' | 'stopped' | 'failed';
export const TERMINAL: ReadonlyArray<BuildState> = ['ready', 'published', 'stopped', 'failed'];
export const BUILD_ID = /^bj_[0-9a-f]{32}$/;

export interface PhaseView {
  step: string;
  state: 'done' | 'running' | 'next' | 'skipped' | 'interrupted';
  started_at: string | null;
  finished_at: string | null;
  rounds: number | null;
  counts: Record<string, number> | null;
  resumed: boolean;
}

/** What every viewer gets (an admin who is not the owner gets only this). */
export interface BuildExistence {
  job_id: string;
  owner_id: string;
  state: BuildState;
  cost_usd: string;
  reason_code: string | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  as_of: string;
  owner_name: string | null;
  queue_position?: number | null;         // only with the workspace view (an admin, a personal workspace)
}

/** The owner's view (`name` is present only here). */
export interface BuildView extends BuildExistence {
  name: string;
  waiting_for_slot: boolean;
  stopping: boolean;
  connection_id: string;
  current_step: string | null;
  draft_id: string | null;
  table_count: number;
  document_count: number;
  progress: { steps: PhaseView[]; percent: number };
  estimate: { total_s: number; left_s: number; basis: 'usual' | 'slower_than_usual' } | null;
  warehouse_bytes: number | null;
  /** Snowflake's statement time (#1999 S3), in seconds; null elsewhere. */
  warehouse_seconds?: number | null;
  documents_read: { read: number; restricted: number; missing: number; refused: number } | null;
  /** The owner's change signals for the questions list (#1624 R8–R9). */
  questions?: { open: number; total: number; answers: number; reads: number };
}

export interface OptionFields {
  table?: string; column?: string; tables?: string[]; columns?: string[]; cardinality?: string;
  aggregation?: string; pattern?: string; snapshot_column?: string;
  start?: string; end?: string; period?: string; absent?: true;
}

/** One reading of a source question: where it came from and its fields (identifiers and enums only). */
export interface SourceOption {
  origin: { kind: 'document' | 'catalog' | 'data'; filename?: string; line?: number; page?: number };
  fields: OptionFields;
  hidden?: Array<'formula' | 'filter_values'>;
  differs_in?: Array<'formula' | 'filter_values'>;
}

/** One reading of a plan question: a kept measure, by its business name and its fields; #2138: which rows it reads. */
export interface PlanOption {
  metric_name: string;
  /** #2180: from the metric BUILD wrote, for the build's owner: W21's one exception. */
  display_name?: string;
  formula?: string;
  fields: OptionFields;
  hidden?: Array<'formula' | 'filter_values'>;
  differs_in?: Array<'formula' | 'filter_values'>;
  all_rows?: true;
  where?: Array<{ column: string; values: string[] }>;
}

/** An answer: an option's index, keep both ("not sure yet — keep asking") or keep all ("different measures", #2138). */
export type AnswerChoice = number | 'keep_both' | 'keep_all';

export interface Answer { choice: AnswerChoice; seq: number; answered_at: string; read: { phase: string; read_at: string } | null; unbound?: true }

/** A build's question as its owner sees it: the server's closed display projection, structure only (W21). */
export interface Question {
  question_id: string;
  kind: 'plan' | 'source';
  answerable: boolean;
  options?: Array<SourceOption | PlanOption>;
  suggested?: number;
  metric_name?: string;
  about?: { kind: 'join' | 'primary_key' | 'grain' | 'load' | 'metric' | 'history' | 'snapshot' | 'measures' | 'route' | 'role'
    | 'lookup' | 'duration' | 'membership' | 'date_basis'; tables: string[] };
  /** #2138: a measure question's class, its term (only when the owner's own material names it), and whether keep all is an answer. */
  decision_class?: 'term' | 'definition';
  term?: string;
  keep_all?: true;
  answer: Answer | null;
}

export type AnyBuild = BuildExistence | BuildView;
export const isOwnerView = (b: AnyBuild): b is BuildView => typeof (b as BuildView).name === 'string';
export const isTerminal = (b: AnyBuild) => TERMINAL.includes(b.state);

export interface BuildList { jobs: AnyBuild[]; running: number; max_running: number; as_of: string }

/** A read with a validator: `changed: false` is a 304 (keep what is shown). */
export type Read<T> = { changed: true; data: T; etag: string | null } | { changed: false; etag: string | null };

export class BuildsError extends Error {
  code: string;
  status: number;

  constructor(code: string, status: number) {
    super(code);
    this.name = 'BuildsError';
    this.code = code;
    this.status = status;
  }
}

const refused = (e: any): never => {
  if (e instanceof BuildsError) throw e;
  const status = e?.response?.status ?? 0;
  const detail = e?.response?.data?.detail;
  const code = typeof detail?.code === 'string' ? detail.code : status === 404 ? 'not_found' : status ? 'refused' : 'unreachable';
  throw new BuildsError(code, status);
};

const STATES: ReadonlyArray<string> = ['queued', 'running', ...TERMINAL];
const buildOf = (b: any): AnyBuild => {
  if (!b || typeof b.job_id !== 'string' || !STATES.includes(b.state) || typeof b.as_of !== 'string') throw new BuildsError('bad_reply', 0);
  if (typeof b.name === 'string' && (!b.progress || !Array.isArray(b.progress.steps))) throw new BuildsError('bad_reply', 0);
  return b as AnyBuild;
};

async function readValidated<T>(url: string, etag: string | null, check: (data: any) => T, params?: object): Promise<Read<T>> {
  try {
    const res = await apiClient.get(url, { params, headers: etag ? { 'If-None-Match': etag } : {}, validateStatus: (s) => s === 200 || s === 304 });
    const tag = (res.headers?.etag as string | undefined) ?? null;
    if (res.status === 304) return { changed: false, etag: tag ?? etag };
    return { changed: true, data: check(res.data), etag: tag };
  } catch (e) {
    return refused(e);
  }
}

export const getBuild = (jobId: string, opts: { brief?: boolean; etag?: string | null } = {}): Promise<Read<AnyBuild>> =>
  readValidated(`/api/builder/jobs/${encodeURIComponent(jobId)}`, opts.etag ?? null, buildOf, opts.brief ? { view: 'brief' } : undefined);

/** `mine` (the sidebar and Home, #1625): only the viewer's own builds and their own running count. */
export const listBuilds = (etag: string | null = null, opts: { mine?: boolean } = {}): Promise<Read<BuildList>> =>
  readValidated('/api/builder/jobs', etag, (d: any) => {
    if (!d || !Array.isArray(d.jobs) || typeof d.running !== 'number' || typeof d.as_of !== 'string') throw new BuildsError('bad_reply', 0);
    return { ...d, jobs: d.jobs.map(buildOf) } as BuildList;
  }, opts.mine ? { mine: 1 } : undefined);

/**
 * Every build mutation tells these listeners once it succeeded (#1625 N12): the shell's summary (sidebar, Home, toasts)
 * reads again, so no count it shows is older than the change the viewer just made.
 */
type Mutated = (build?: AnyBuild, namespace?: string | null) => void;
const mutated = new Set<Mutated>();
/** `build`: the build as the mutation's reply reports it, when it reports one (stop, remove, rename); `namespace`: the
 * account and workspace the mutation was sent in (a reply landing after a switch belongs to the old one). */
export function onBuildMutation(listener: Mutated): () => void {
  mutated.add(listener);
  return () => { mutated.delete(listener); };
}
/** Tell the listeners a build changed (every module that changes a build calls this after its reply). */
export function buildMutated(build?: AnyBuild, namespace?: string | null): void {
  mutated.forEach((listener) => { try { listener(build, namespace); } catch { /* a listener's failure is its own */ } });
}
function changed<T>(value: T, namespace: string | null): T {
  buildMutated(value && typeof value === 'object' && 'job_id' in (value as any) && 'state' in (value as any) ? value as unknown as AnyBuild : undefined, namespace);
  return value;
}

const act = async (jobId: string, verb: 'stop' | 'remove' | 'rename', body?: object): Promise<AnyBuild> => {
  const ns = getPersistenceNamespace();
  try {
    const { data } = await apiClient.post(`/api/builder/jobs/${encodeURIComponent(jobId)}/${verb}`, body);
    return changed(buildOf(data), ns);
  } catch (e) {
    return refused(e);
  }
};

const questionsOf = (d: any): Question[] => {
  if (!d || !Array.isArray(d.questions) || !d.questions.every((q: any) => q && typeof q.question_id === 'string' && typeof q.answerable === 'boolean')) {
    throw new BuildsError('bad_reply', 0);
  }
  return d.questions as Question[];
};

/** The owner's questions (anyone else: 404, as a build that is not there). */
export const getQuestions = async (jobId: string): Promise<Question[]> => {
  try {
    const { data } = await apiClient.get(`/api/builder/jobs/${encodeURIComponent(jobId)}/questions`);
    return questionsOf(data);
  } catch (e) {
    return refused(e);
  }
};

/** Save an answer: one of the question's own options by index, keep both readings, or keep all (#2138). */
export const answerQuestion = async (jobId: string, questionId: string, choice: AnswerChoice): Promise<Question[]> => {
  const ns = getPersistenceNamespace();
  try {
    const { data } = await apiClient.post(`/api/builder/jobs/${encodeURIComponent(jobId)}/questions/${encodeURIComponent(questionId)}/answer`,
      choice === 'keep_both' ? { keep_both: true } : choice === 'keep_all' ? { keep_all: true } : { choice });
    return changed(questionsOf(data), ns);
  } catch (e) {
    return refused(e);
  }
};

export const stopBuild = (jobId: string) => act(jobId, 'stop');
export const removeBuild = (jobId: string) => act(jobId, 'remove');
export const renameBuild = (jobId: string, name: string) => act(jobId, 'rename', { name });

/** A build's notice as the app shows it (#1625 R3): one per claim; `more` others are named by it. */
export type NoticeKind = 'questions' | 'ready' | 'failed' | 'stopped';
export interface Notice { kind: NoticeKind; job_id: string; name: string; open_questions: number; created_at: string }
export interface Claimed { toast: Notice | null; more: number; lease: string | null }

const KINDS: ReadonlyArray<string> = ['questions', 'ready', 'failed', 'stopped'];
const claimedOf = (d: any): Claimed => {
  const t = d?.toast;
  if (!d || typeof d.more !== 'number' || (t !== null && (!t || !KINDS.includes(t.kind) || !BUILD_ID.test(t.job_id)
    || typeof t.name !== 'string' || typeof t.open_questions !== 'number')) || (t !== null && typeof d.lease !== 'string')) {
    throw new BuildsError('bad_reply', 0);
  }
  return d as Claimed;
};

/** Lease the viewer's unshown notices; one toast covers them all (`onPage`: the build on screen is told there). */
export const claimNotices = async (onPage: string | null): Promise<Claimed> => {
  try {
    const { data } = await apiClient.post('/api/builder/notifications/claim', { on_page: onPage });
    return claimedOf(data);
  } catch (e) {
    return refused(e);
  }
};

/** The toast of `lease` is on screen: its notices are shown. */
export const ackNotices = async (lease: string): Promise<void> => {
  try {
    await apiClient.post('/api/builder/notifications/ack', { lease });
  } catch (e) {
    refused(e);
  }
};

export type NotifyChannel = 'in_app' | 'email' | 'slack';
export interface NotifySettings {
  in_app: boolean; email: boolean; slack: boolean;
  available: { email: { ok: boolean; reason: string | null }; slack: { ok: boolean; reason: string | null } };
}

export const getNotifySettings = async (): Promise<NotifySettings> => {
  try {
    const { data } = await apiClient.get('/api/builder/notification-settings');
    if (!data || typeof data.in_app !== 'boolean' || !data.available) throw new BuildsError('bad_reply', 0);
    return data as NotifySettings;
  } catch (e) {
    return refused(e);
  }
};

export const setNotifySetting = async (channel: NotifyChannel, on: boolean): Promise<NotifySettings> => {
  try {
    const { data } = await apiClient.put(`/api/builder/notification-settings/${channel}`, { on });
    return data as NotifySettings;
  } catch (e) {
    return refused(e);
  }
};
